<template>
  <div class="pagination-container" v-if="!props.fixedRowsPerPage || totalRows > rowsPerPage">
    <div class="spacer" v-if="!props.fixedRowsPerPage"/>

    <Paginator
        :first="(currentPage - 1) * rowsPerPage"
        :rows="rowsPerPage"
        :total-records="totalRows"
        :alwaysShow="false"
        @page="onPaginatorPage"
        class="paginator-nav"
    />

    <div class="rows-per-page" v-if="!props.fixedRowsPerPage">
      <div>
        <span class="rows-per-page-text">{{ t("ROWS_PER_PAGE") }}</span>
        <Select
            v-model="rowsPerPage"
            :options="rowsPerPageOpts"
            option-label="label"
            option-value="value"
            class="rows-per-page-select"
        />
      </div>
    </div>
  </div>

  <div class="table-container" :id="props.id">
    <DataTable
        :value="rows"
        :striped-rows="true"
        :size="small ? 'small' : 'normal'"
        :class="props.tableClass"
        :sort-field="currentSort.sortBy"
        :sort-order="sortOrderNum"
        :lazy="true"
        :rowHover="true"
        :removableSort="true"
        data-key="id"
        @sort="onDataTableSort"
    >
      <Column
          v-for="f in props.fields"
          :field="f.key"
          :header="f.label"
          :sortable="f.sortable !== false"
      >
        <template #body="{data, index}">
          <slot :name="`cell(${f.key})`" :item="data" :index="index">
            {{ getValueByPath(data, f) }}
          </slot>
        </template>
      </Column>
      <template #empty>
        {{ loading ? `${t('LOADING_RESULTS')}...` : t('NO_RESULTS') }}
      </template>
    </DataTable>

    <div class="mt-4" v-if="totalRows > rowsPerPage">
      <Paginator
          :first="(currentPage - 1) * rowsPerPage"
          :rows="rowsPerPage"
          :total-records="totalRows"
          :alwaysShow="false"
          @page="onPaginatorPage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {type LocationQueryRaw, useRoute, useRouter} from "vue-router";
import type {DataTablePageEvent} from "primevue";

export interface PaginationRequest {
  page: number;
  size: number;
  sort?: string;
  searchValue?: string;
}

interface PaginationResponse {
  data: {
    content: object[];
    totalSize: number;
  };
}

interface SortEvent {
  sortBy?: string;
  sortDesc?: boolean;
}

interface ValuesToSave {
  searchValue?: string;

  [key: string]: unknown;
}

interface FieldDef {
  key: string;
  label: string;
  sortable?: boolean;
  formatter?: (v: any, key: string, item: any) => any;
}

const STORAGE_KEY_ROWS_PER_PAGE = "pagination__rowsPerPage";

const {t} = useI18n();

const rowsPerPageOpts = [
  {value: 15, label: "15"},
  {value: 30, label: "30"},
  {value: 45, label: "45"},
  {value: 60, label: "60"},
  {value: 75, label: "75"},
  {value: 90, label: "90"},
];

const props = withDefaults(
    defineProps<{
      id: string;
      tableClass?: string;
      rowsPerPage?: (typeof rowsPerPageOpts)[number]["value"];
      fixedRowsPerPage?: (typeof rowsPerPageOpts)[number]["value"];
      small?: boolean;
      onPageChange: (data: PaginationRequest) => Promise<PaginationResponse>;
      fields: FieldDef[];
      valuesToSave?: ValuesToSave;
    }>(),
    {
      rowsPerPage: 15,
      small: false,
    }
);

const formatStorageKey = () => {
  return `${STORAGE_KEY_ROWS_PER_PAGE}_${props.id}`;
};

const getValueByPath = (obj: object, f: FieldDef) => {
  const result = f.key.split('.').reduce((acc, key) => acc?.[key], obj);
  return (result && f.formatter ? f.formatter(result, f.key, obj) : result) ?? null;
};

const getInitRowsPerPage = () => {
  if (props.fixedRowsPerPage) {
    return props.fixedRowsPerPage;
  }

  const defaultVal = props.rowsPerPage || rowsPerPageOpts[0].value;
  const val = localStorage.getItem(formatStorageKey());
  return val
      ? rowsPerPageOpts.find((r) => Number.parseInt(val) === r.value)?.value || defaultVal
      : defaultVal;
};

const route = useRoute();
const router = useRouter();

const currentPage = ref(Number(route.query.page ?? 1));
const rows = ref<object[]>([]);
const rowsSaved = ref<object[]>([]);
const rowsPerPage = ref(getInitRowsPerPage());
const totalRows = ref(0);
const loading = ref(false);
const searchValue = ref(props.valuesToSave?.searchValue ?? "");
const currentSort = ref<SortEvent>({
  sortBy: route.query.sortBy?.toString(),
  sortDesc: route.query.sortDesc === "true",
});

const sortOrderNum = computed(() => (currentSort.value.sortDesc ? -1 : 1));

onMounted(async () => {
  await fetchRows();
});

const onDataTableSort = async (event: { sortField: string; sortOrder: number }) => {
  currentPage.value = 1;
  currentSort.value = {
    sortBy: event.sortField,
    sortDesc: event.sortOrder === -1,
  };
  await fetchRows();
};

const onPaginatorPage = async (event: DataTablePageEvent) => {
  currentPage.value = event.page + 1;
  await fetchRows();
};

const onSearch = async (value: string) => {
  currentPage.value = 1;
  searchValue.value = value;
  await fetchRows();
};

const reset = async () => {
  currentSort.value = {};
  currentPage.value = 1;
  searchValue.value = "";
  await fetchRows();
};

const fetchRows = async () => {
  const params: PaginationRequest = {
    page: currentPage.value - 1,
    size: rowsPerPage.value,
  };

  if (currentSort.value.sortBy) {
    params["sort"] = `${currentSort.value.sortBy},${currentSort.value.sortDesc ? "desc" : "asc"}`;
  }

  if (searchValue.value) {
    params["searchValue"] = searchValue.value;
  }

  loading.value = true;

  try {
    const resp = await props.onPageChange(params);
    rows.value = resp.data.content ?? [];
    rowsSaved.value = rows.value.map(r => ({...r}));
    totalRows.value = resp.data.totalSize;
  } finally {
    loading.value = false;
  }

  if (props.valuesToSave) {
    const queryParams = {
      ...props.valuesToSave,
      page: currentPage.value,
      sortBy: currentSort.value.sortBy,
      sortDesc: currentSort.value.sortDesc ? String(currentSort.value.sortDesc) : undefined,
    };

    await router.push({path: route.path, query: queryParams as LocationQueryRaw});
  }
};

const valueChanged = (item: object, key: string, index: number) => {
  return rowsSaved.value[index]?.[key] !== item[key];
};

watch(rowsPerPage, () => {
  localStorage.setItem(formatStorageKey(), rowsPerPage.value.toString());
  reset();
});

defineExpose({
  onSearch,
  reset,
  fetchRows,
  valueChanged,
  numberOfResults: () => totalRows.value,
});
</script>

<style lang="scss" scoped>
.pagination-container {
  margin: 0.75rem 0;
  padding: 0.75rem;
  border: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  justify-content: center;

  &:has(.rows-per-page) {
    justify-content: space-between;
  }

  :deep(.p-paginator) {
    margin: 0 0.5rem;
    align-self: center;
  }

  @media (max-width: 991px) {
    > .spacer {
      display: none;
    }

    :deep(.p-paginator) {
      margin-left: 0;
    }
  }

  > .rows-per-page {
    display: flex;
    justify-content: end;

    > div {
      display: flex;
      align-items: stretch;
      height: 100%;

      > span {
        border: 1px solid #f0f1f2;
        padding: 0.75rem;
        border-bottom-left-radius: 9px;
        border-top-left-radius: 9px;
        font-size: 14px;
        white-space: nowrap;
      }

      :deep(.p-select) {
        border-bottom-right-radius: 9px;
        border-top-right-radius: 9px;
        font-size: 14px;
        border: 1px solid #f0f1f2;
      }
    }
  }
}

.pagination-container:has(nav.paginator-nav) .rows-per-page-text {
  display: none;
}

.pagination-container:not(:has(nav.paginator-nav)) .rows-per-page-select {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: -1px;
}

@media (min-width: 767px) {
  .pagination-container:has(nav.paginator-nav) .rows-per-page-text {
    display: inline;

    & + .rows-per-page-select {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      margin-left: -1px;
    }
  }
}
</style>
